// Kalenderweergave van Projecttaken (planning-gauntlet stap 3, 25 sep 2026).
//
// Maand- of twee-wekenraster ma–vr met het weekend smal. Per dag rustige kaartjes: taaknaam
// (max. twee regels), eronder klein en grijs het project, en één gedempte tint per project
// als dunne streep links. Geen avatars, iconen of prio-kleuren; rood alleen voor een
// verlopen deadline. Een meerdaagse taak is één balk over zijn dagen. Klik opent het
// TaakPaneel; slepen naar een andere dag verzet start én eind (looptijd blijft).
import { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  wekenInBeeld, plaatsInWeek, beperkBanen, isWeekend, periodeVan, tintVoor, bereikLabel, verschuifAnker,
} from './kalenderLogica';

const FONT = '"Inter", system-ui, sans-serif';
const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const ZACHT = '#9ca3af';
const LIJN = '#eceef1';
const KAART = '#f8fafc';
const ACCENT = '#526075';
const ROOD = '#b91c1c';
const WEEKEND = 'rgba(15,23,42,0.015)';

const DAGNAMEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];
const KOLOMMEN = 'repeat(5, minmax(0, 1fr)) 34px 34px';

const stilKnop = {
  border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 13,
  color: GEDEMPT, '&:hover': { color: TEKST },
};

/** De dag onder de muis bij het oppakken (een meerdaagse balk ligt over meerdere dagen). */
function dagOnderMuis(e) {
  const punten = typeof document.elementsFromPoint === 'function'
    ? document.elementsFromPoint(e.clientX, e.clientY) : [];
  const cel = punten.find((el) => el?.dataset?.dag);
  return cel ? cel.dataset.dag : null;
}

function Kaart({ item, vandaag, gekozen, onOpen }) {
  const { taak, kolVan, kolTot, baan, doorLinks, doorRechts } = item;
  const teLaat = !taak.afgerond && taak.deadline && taak.deadline < vandaag;
  const tint = tintVoor(taak.projectId || taak.projectNaam);
  const smal = kolVan >= 5 && kolTot >= 5; // alleen in het weekend: te smal voor tekst
  return (
    <Box
      role="button" tabIndex={0} draggable
      aria-label={`Open ${taak.naam}`}
      data-taak={taak.id}
      data-dagen={item.van === item.tot ? item.van : `${item.van}..${item.tot}`}
      data-te-laat={teLaat ? 'ja' : undefined}
      title={[taak.naam, taak.projectNaam, taak.deadline ? `deadline ${taak.deadline}` : ''].filter(Boolean).join(' · ')}
      onClick={() => onOpen(taak)}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onOpen(taak); } }}
      onDragStart={(e) => {
        const p = periodeVan(taak);
        const gepakt = dagOnderMuis(e) || p?.van;
        e.dataTransfer.setData('text/plain', JSON.stringify({ id: taak.id, gepakt }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      sx={{
        gridRow: baan + 2, gridColumn: `${kolVan + 1} / ${kolTot + 2}`, zIndex: 1, minWidth: 0,
        mx: 0.5, mb: 0.5, px: smal ? 0.5 : 0.9, py: 0.5, cursor: 'pointer',
        bgcolor: gekozen ? '#eef1f5' : KAART,
        borderLeft: doorLinks ? 0 : `3px solid ${tint}`,
        borderRadius: `${doorLinks ? 0 : 4}px ${doorRechts ? 0 : 4}px ${doorRechts ? 0 : 4}px ${doorLinks ? 0 : 4}px`,
        '&:hover': { bgcolor: '#eef1f5' },
        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: -2 },
      }}
    >
      {!smal && (
        <>
          <Typography sx={{
            fontFamily: FONT, fontSize: 12.5, lineHeight: 1.35, color: teLaat ? ROOD : TEKST,
            fontWeight: teLaat ? 600 : 500,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            wordBreak: 'break-word',
          }}>
            {taak.naam}
          </Typography>
          {taak.projectNaam && (
            <Typography noWrap sx={{ fontFamily: FONT, fontSize: 11, color: ZACHT, lineHeight: 1.35 }}>
              {taak.projectNaam}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

function Week({ week, index, taken, vandaag, maand, bereik, open, onWissel, gekozenId, onOpen, onVerzet, sleepDoel, setSleepDoel }) {
  const plaatsing = useMemo(() => plaatsInWeek(taken, week), [taken, week]);
  const max = open ? 0 : (bereik === '2w' ? 8 : 3);
  const { zichtbaar, verborgen, banen } = beperkBanen(plaatsing, max);
  const heeftMeer = verborgen.some(Boolean);
  const kanMinder = open && plaatsing.banen > 4;

  return (
    <Box
      role="row" aria-label={`Week van ${week[0]}`}
      sx={{
        display: 'grid', gridTemplateColumns: KOLOMMEN, position: 'relative',
        gridTemplateRows: `26px repeat(${banen}, auto) ${heeftMeer || kanMinder ? '22px' : '0px'}`,
        minHeight: bereik === '2w' ? 260 : 118, borderBottom: `1px solid ${LIJN}`, pb: 0.5,
      }}
    >
      {week.map((dag, k) => {
        const weekend = isWeekend(dag);
        const doel = sleepDoel === dag;
        return (
          <Box
            key={dag} role="gridcell" data-dag={dag} aria-label={dag}
            onDragOver={weekend ? undefined : (e) => { e.preventDefault(); if (sleepDoel !== dag) setSleepDoel(dag); }}
            onDragLeave={weekend ? undefined : () => setSleepDoel((d) => (d === dag ? null : d))}
            onDrop={weekend ? undefined : (e) => {
              e.preventDefault();
              setSleepDoel(null);
              let gegevens = null;
              try { gegevens = JSON.parse(e.dataTransfer.getData('text/plain') || 'null'); } catch { /* geen taak */ }
              if (gegevens?.id) onVerzet(gegevens.id, gegevens.gepakt, dag);
            }}
            sx={{
              gridRow: '1 / -1', gridColumn: k + 1, borderLeft: k === 0 ? 0 : `1px solid ${LIJN}`,
              bgcolor: doel ? 'rgba(82,96,117,0.07)' : (weekend ? WEEKEND : 'transparent'),
              transition: 'background-color 0.1s ease',
            }}
          />
        );
      })}
      {week.map((dag, k) => {
        const isVandaag = dag === vandaag;
        const buiten = maand && dag.slice(0, 7) !== maand;
        const dagNr = Number(dag.slice(8, 10));
        return (
          <Typography key={`n-${dag}`} aria-hidden sx={{
            gridRow: 1, gridColumn: k + 1, zIndex: 1, pointerEvents: 'none', px: k >= 5 ? 0.5 : 1, pt: 0.5,
            fontFamily: FONT, fontSize: 12, lineHeight: '20px',
            color: isVandaag ? ACCENT : (buiten || k >= 5 ? ZACHT : GEDEMPT), fontWeight: isVandaag ? 700 : 400,
            textAlign: k >= 5 ? 'center' : 'left',
          }}>
            {dagNr === 1 && k < 5 && bereik === '2w' ? `${dagNr} ${new Date(`${dag}T12:00:00`).toLocaleDateString('nl-NL', { month: 'short' })}` : dagNr}
          </Typography>
        );
      })}
      {zichtbaar.map((it) => (
        <Kaart key={`${it.taak.id}-${index}`} item={it} vandaag={vandaag} gekozen={it.taak.id === gekozenId} onOpen={onOpen} />
      ))}
      {heeftMeer && verborgen.map((n, k) => (n ? (
        <Box key={`m-${week[k]}`} component="button" type="button" onClick={onWissel}
          sx={{ ...stilKnop, gridRow: banen + 2, gridColumn: k + 1, zIndex: 1, fontSize: 11.5, color: ZACHT, textAlign: 'left', px: 1 }}>
          {k >= 5 ? `+${n}` : `+${n} meer`}
        </Box>
      ) : null))}
      {kanMinder && (
        <Box component="button" type="button" onClick={onWissel}
          sx={{ ...stilKnop, gridRow: banen + 2, gridColumn: 1, zIndex: 1, fontSize: 11.5, color: ZACHT, textAlign: 'left', px: 1 }}>
          minder
        </Box>
      )}
    </Box>
  );
}

/**
 * @param taken     rijen uit mijnTakenLijst (id, naam, start, eind, deadline, projectId, projectNaam, afgerond)
 * @param onVerzet  `(id, gepaktOp, doelISO)` — de pagina schrijft en biedt ongedaan maken aan
 */
export default function TakenKalender({ taken, vandaag, gekozenId, onOpen, onVerzet, bereik, onBereik, anker, onAnker }) {
  const weken = useMemo(() => wekenInBeeld(anker, bereik), [anker, bereik]);
  const [openWeken, setOpenWeken] = useState(() => new Set());
  const [sleepDoel, setSleepDoel] = useState(null);
  const maand = bereik === 'maand' ? String(anker).slice(0, 7) : null;
  const zonderDatum = (taken || []).filter((t) => !periodeVan(t)).length;

  const wissel = (i) => setOpenWeken((s) => {
    const n = new Set(s);
    if (n.has(i)) n.delete(i); else n.add(i);
    return n;
  });

  const bereikKnop = (waarde, label) => (
    <Box component="button" type="button" aria-pressed={bereik === waarde} onClick={() => onBereik(waarde)}
      sx={{ ...stilKnop, color: bereik === waarde ? TEKST : ZACHT, fontWeight: bereik === waarde ? 600 : 400 }}>
      {label}
    </Box>
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography component="h2" sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: TEKST, textTransform: 'capitalize', minWidth: 150 }}>
          {bereikLabel(weken, anker, bereik)}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
          <Box component="button" type="button" aria-label="Vorige" onClick={() => onAnker(verschuifAnker(anker, bereik, -1))} sx={stilKnop}>‹</Box>
          <Box component="button" type="button" onClick={() => onAnker(vandaag)} sx={stilKnop}>Vandaag</Box>
          <Box component="button" type="button" aria-label="Volgende" onClick={() => onAnker(verschuifAnker(anker, bereik, 1))} sx={stilKnop}>›</Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
          {bereikKnop('maand', 'Maand')}
          <Typography component="span" sx={{ color: LIJN }}>|</Typography>
          {bereikKnop('2w', '2 weken')}
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box role="grid" aria-label="Kalender" sx={{ minWidth: 640, borderTop: `1px solid ${LIJN}` }}>
          <Box role="row" sx={{ display: 'grid', gridTemplateColumns: KOLOMMEN, borderBottom: `1px solid ${LIJN}` }}>
            {DAGNAMEN.map((d, k) => (
              <Typography key={d} role="columnheader" sx={{
                fontFamily: FONT, fontSize: 11.5, color: ZACHT, py: 0.75, px: k >= 5 ? 0 : 1,
                textAlign: k >= 5 ? 'center' : 'left',
              }}>
                {d}
              </Typography>
            ))}
          </Box>
          {weken.map((week, i) => (
            <Week
              key={week[0]} week={week} index={i} taken={taken} vandaag={vandaag} maand={maand} bereik={bereik}
              open={openWeken.has(i)} onWissel={() => wissel(i)}
              gekozenId={gekozenId} onOpen={onOpen} onVerzet={onVerzet}
              sleepDoel={sleepDoel} setSleepDoel={setSleepDoel}
            />
          ))}
        </Box>
      </Box>

      {zonderDatum > 0 && (
        <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: ZACHT, mt: 1.5 }}>
          {zonderDatum === 1 ? '1 taak zonder datum staat alleen in de lijst.' : `${zonderDatum} taken zonder datum staan alleen in de lijst.`}
        </Typography>
      )}
    </Box>
  );
}
